import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { AuditLogService, FieldChangeRow } from './audit-log.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatExpansionPanel } from '@angular/material/expansion';
import { CommonApiService } from '../../common-api.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-audit-log-viewer',
  templateUrl: './audit-log-viewer.component.html',
  styleUrls: ['./audit-log-viewer.component.css'],
})
export class AuditLogViewerComponent implements OnChanges, OnInit, OnDestroy {
  @Input() model!: string;
  @Input() recordId!: number | string | null | undefined;
  @Input() fieldLabels: Record<string, string> = {};

  dataSource = new MatTableDataSource<FieldChangeRow>([]);
  loaded = false;
  loading = false;
  displayedColumns = ['id', 'changed_at', 'operation', 'field', 'oldValue', 'newValue', 'created_by'];

  @ViewChild(MatExpansionPanel) private panel?: MatExpansionPanel;
  @ViewChild(MatPaginator) paginator?: MatPaginator;

  private refreshSub?: Subscription;

  constructor(private audit: AuditLogService, private api: CommonApiService) {}

  ngOnInit(): void {
    this.refreshSub = this.api._refreshNeeded$.subscribe(() => {
      if (this.loaded && this.recordId) {
        this.reload();
      }
    });
  }

  ngOnDestroy(): void {
    this.refreshSub?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['recordId'] && this.loaded) {
      this.loaded = false;
    }
  }

  ensureLoaded(): void {
    if (!this.loaded) {
      this.reload();
    }
  }

  reload(): void {
    if (this.recordId === null || this.recordId === undefined || this.recordId === '') return;
    if (!this.model) return;
    const rid = typeof this.recordId === 'string' ? parseInt(this.recordId, 10) : this.recordId;
    if (isNaN(rid as number)) return;
    this.loading = true;
    this.audit.fetch(this.model, rid as number).subscribe({
      next: (entries) => {
        const flat = this.audit.flatten(entries);
        this.dataSource = new MatTableDataSource<FieldChangeRow>(flat);
        this.loaded = true;
        this.loading = false;
        // Defer paginator assignment so the ViewChild resolves after *ngIf shows the table
        setTimeout(() => {
          if (this.paginator) {
            this.dataSource.paginator = this.paginator;
          }
        });
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  /** ISO 8601 date pattern: e.g. 2026-02-21T00:00:00+05:30, 2026-02-20T18:30:00.000Z */
  private readonly isoDatePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

  formatValue(v: any): string {
    if (v === null || v === undefined) return '';
    if (typeof v === 'object') return JSON.stringify(v);
    const s = String(v);
    // Detect and format ISO date strings into a user-friendly format
    if (this.isoDatePattern.test(s)) {
      const d = new Date(s);
      if (!isNaN(d.getTime())) {
        // Date-only field: time part is T00:00:00 in the original string (midnight in its timezone)
        // or the JS Date resolves to UTC midnight (handles old data stored as midnight UTC)
        const isDateOnly = /T00:00:00/.test(s)
          || (d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0);
        if (isDateOnly) {
          return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
        }
        return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' })
          + ', ' + d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
      }
    }
    return s;
  }

  formattedCell(row: FieldChangeRow, which: 'old' | 'new'): string {
    const rawKey = which === 'old' ? 'oldValue' : 'newValue';
    return this.formatValue((row as any)[rawKey]);
  }

  displayField(field: string): string {
    if (this.fieldLabels && this.fieldLabels[field]) return this.fieldLabels[field];
    let base = field.endsWith('_id') ? field.slice(0, -3) : field;
    base = base.replace(/_/g, ' ');
    return base.replace(/\b\w/g, (c) => c.toUpperCase());
  }

  doFilter = (event: Event) => {
    const value = (event.target as HTMLInputElement).value;
    if (typeof value === 'string') {
      this.dataSource.filter = value.trim().toLocaleLowerCase();
    }
  };
}
